import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft,
  Settings,
  Layers,
  Package,
  Plus
} from 'lucide-react';
import api from '@/utils/api';

interface VariantManagerProps {
  productId?: string;
}

export function VariantManager({ productId: propProductId }: VariantManagerProps) {
  const { productId: paramProductId } = useParams();
  const productId = propProductId || paramProductId;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch product data
  const { data: product, isLoading } = useQuery({
    queryKey: ['product', productId],
    queryFn: async () => {
      const { data } = await api.get(`/api/products/${productId}`);
      return data;
    },
    enabled: !!productId,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="text-center py-8">
        <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Product not found</h3>
        <Button onClick={() => navigate('/dashboard/products')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Products
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard/products')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Manage Variants</h1>
            <p className="text-muted-foreground">{product.name}</p>
          </div>
        </div>
      </div>

      {/* Product Options */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Product Options
            </CardTitle>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Add Option
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {product.options && product.options.length > 0 ? (
            <div className="space-y-4">
              {product.options.map((option: any, index: number) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium">{option.name}</h4>
                      {option.required && (
                        <Badge variant="secondary">Required</Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {option.values.map((value: any, valueIndex: number) => (
                      <Badge key={valueIndex} variant="outline">
                        {value.name}
                        {value.price_adjustment !== 0 && (
                          <span className="ml-1">
                            ({value.price_adjustment > 0 ? '+' : ''}${value.price_adjustment})
                          </span>
                        )}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Settings className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No options yet
              </h3>
              <p className="text-gray-500 mb-4">
                Add options to create product variants
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Product Variants */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5" />
              Product Variants ({product.variants?.length || 0})
            </CardTitle>
            <div className="flex gap-2">
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                Add Variant
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {product.variants && product.variants.length > 0 ? (
            <div className="space-y-4">
              {product.variants.map((variant: any, index: number) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {Object.entries(variant.options || {}).map(([key, value]) => (
                          <Badge key={key} variant="outline">
                            {key}: {value as string}
                          </Badge>
                        ))}
                      </div>
                      <div className="flex gap-4 text-sm text-gray-600">
                        <span>SKU: {variant.sku || '—'}</span>
                        <span>Price: ${variant.price}</span>
                        <span>Stock: {variant.inventory}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Layers className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No variants yet
              </h3>
              <p className="text-gray-500 mb-4">
                {product.options && product.options.length > 0
                  ? 'Generate variants from your product options or create them manually'
                  : 'Add product options first to create variants'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}